/* taken from react-lazyload */

import React, {Component} from 'react';
import ReactDom from 'react-dom';
import {on, off} from './utils/event';

import {
    findIndex
} from '../../../../utils/array';
import {
    addListener as addScrollListener,
    isScrollBeingMonitored,
    monitorScroll
} from '../../../../utils/scroll';

const defaultBoundingClientRect = {top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0};
const listeners = [];
const eventListeners = [];
let pending = [];

// All events we want to listen to and call our `lazyLoadListener: Function`
const watcherEvents = ['resize'];

let passiveEventSupported;
try {
    const opts = Object.defineProperty({}, 'passive', {
        get() {
            passiveEventSupported = true;
        }
    });
    window.addEventListener('test', null, opts);
} catch (e) {
    passiveEventSupported = false;
}

// if they are supported, setup the optional params
// IMPORTANT: FALSE doubles as the default CAPTURE value!
const passiveEvent = passiveEventSupported ? {capture: false, passive: true} : false;

function checkNormalVisible(component) {
    const node = ReactDom.findDOMNode(component);

    if (!node) {
        return false;
    }

    // Try our best to avoid layout thrashing by grouping "layout triggers" together...
    const rect = node.getBoundingClientRect();
    const rectsLength = node.getClientRects().length;
    const offsetHeight = node.offsetHeight;
    const offsetWidth = node.offsetWidth;
    const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowInnerWidth = window.innerWidth || document.documentElement.clientWidth;

    if (!(offsetWidth || offsetHeight || rectsLength)) {
        return false;
    }

    let top;
    let elementHeight;
    let left;

    try {
        ({top, height: elementHeight, left} = rect);
    } catch (e) {
        ({top, height: elementHeight} = defaultBoundingClientRect);
    }

    const offset = component.props.offset;

    return (top - offset <= windowInnerHeight) && (top + elementHeight + offset >= 0) && (left - offset <= windowInnerWidth);
}

function checkVisible(component) {
    const visible = checkNormalVisible(component);

    // Avoid extra render if previously is visible
    if (visible && !component.visible) {
        pending.push(component);
        component.visible = true;
        component.forceUpdate();
    }
}

function purgePending() {
    if (!pending.length) {
        return;
    }

    pending.forEach((component) => {
        const index = listeners.indexOf(component);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    });

    pending = [];
}

function lazyLoadHandler() {
    for (let i = 0; i < listeners.length; ++i) {
        const listener = listeners[i];

        checkVisible(listener);
    }

    purgePending();
}

const observer = typeof MutationObserver !== 'undefined'
    ? new MutationObserver((list) => {
        Array.from(list).some((mutation) => {
            const {attributeName, type} = mutation;
            if (type === 'attributes' && attributeName === 'style') {
                lazyLoadHandler();
                return true;
            }
            return false;
        });
    })
    : null;

class LazyLoad extends Component {
    constructor(props) {
        super(props);

        const {initializeAsVisible: visible} = this.props;
        this.visible = typeof visible === 'boolean' ? visible : false;
    }

    componentDidMount() {
        if (listeners.length === 0) {
            this.handlingWatchers(window, on, watcherEvents);
        }

        this.addElementListener();

        listeners.push(this);
        checkVisible(this);
    }

    shouldComponentUpdate() {
        return this.visible;
    }

    componentDidUpdate(prevProps) {
        const {addListener: prevAddListener = {}} = prevProps;
        const {addListener = {}} = this.props;

        if (prevAddListener.event !== addListener.event) {
            this.removeElementListener(prevAddListener.event);

            setImmediate(() => this.addElementListener(addListener.event));
        }
    }

    componentWillUnmount() {
        const index = listeners.indexOf(this);
        if (index !== -1) {
            listeners.splice(index, 1);
        }

        if (listeners.length === 0) {
            this.handlingWatchers(window, off, watcherEvents);

            this.removeElementListener();
        }
    }

    addElementListener(eventType) {
        if (this.props.addListener && this.props.addListener.element) {
            const {addListener} = this.props;
            const carousel = document.querySelector(addListener.element) || null;

            eventType = eventType || addListener.event;

            if (carousel) {
                // MutationObserver#observe will error if the `carousel.nodeType !== Element.ELEMENT_NODE`
                if (observer && carousel.nodeType === 1) {
                    observer.observe(carousel, {
                        attributes: true,
                        childList: false,
                        subtree: false
                    });
                }

                // MSIE 11 does not have String.prototype.startsWith!!!
                if (eventType.indexOf('scroll') !== 0) {
                    let carouselIndex = findIndex(eventListeners, ({element, event}) => element === carousel && event === eventType);

                    if (carouselIndex < 0) {
                        carouselIndex = eventListeners.push({count: 0, element: carousel, event: eventType}) - 1;

                        this.handlingWatchers(carousel, on, eventType);
                    }

                    ++eventListeners[carouselIndex].count;
                }
            }
        }
    }

    removeElementListener(eventType) {
        if (this.props.addListener && this.props.addListener.element) {
            const {addListener} = this.props;
            const carousel = document.querySelector(addListener.element) || null;

            eventType = eventType || addListener.event;

            // MSIE 11 does not have String.prototype.startsWith!!!
            if (carousel && eventType.indexOf('scroll') !== 0) {
                const carouselIndex = findIndex(eventListeners, ({element, event}) => element === carousel && event === eventType);

                if (carouselIndex > -1) {
                    if (--eventListeners[carouselIndex].count < 1) {
                        eventListeners.splice(carouselIndex, 1);
                        this.handlingWatchers(carousel, off, eventType);
                    }
                }
            }
        }
    }

    handlingWatchers(element, func, event) {
        if (Array.isArray(event)) {
            event.forEach((evt) => this.handlingWatchers(element, func, evt));

            return;
        }

        func(element, event, lazyLoadHandler, passiveEvent);
    }

    render() {
        const defaultPlaceholder = <div style={{height: this.props.height}} className="lazyload-placeholder" />;
        if (this.visible) {
            return this.props.children;
        } else if (this.props.placeholder) {
            return this.props.placeholder || defaultPlaceholder;
        }

        return defaultPlaceholder;
    }
}

export default LazyLoad;

if (typeof navigator !== 'undefined') {
    if (!isScrollBeingMonitored()) {
        monitorScroll(true);
    }

    addScrollListener(lazyLoadHandler);
}
